import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import app from "../firebaseConfig";
import exampleImage from './example1.jpg';
import exampleImage2 from './example2.jpg';
import back7 from './back1.png';  // Imported but not used since background removed

const db = getFirestore(app);

const RentalProcessPage = () => {
  const { carID } = useParams();
  const [car, setCar] = useState(null);
  const [step, setStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [formData, setFormData] = useState({
    pickupDate: new Date(),
    returnDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
    addOns: [],
    hasInsurance: null,
    fullCoverage: false,
    insuranceFile: null,
    idFile: null,
    selfieFile: null,
    rentalAgreementSigned: false,
    cardNumber: "",
    cardExpiry: "",
    cardCVV: "",
    cardHolderName: ""
  });

  useEffect(() => {
    const fetchCarDetails = async () => {
      try {
        const carDoc = await getDoc(doc(db, "cars", carID));
        if (carDoc.exists()) {
          setCar(carDoc.data());
        } else {
          console.error("Car not found!");
        }
      } catch (error) {
        console.error("Error fetching car details:", error);
      }
    };

    fetchCarDetails();
  }, [carID]);

  const handleNext = () => {
    if (
      step === 3 &&
      formData.hasInsurance === true &&
      (!formData.insuranceFile || !formData.idFile || !formData.selfieFile)
    ) {
      setErrorMessage(
        "Please upload your insurance details, ID, and a selfie with your ID."
      );
      return;
    }
    if (
      step === 3 &&
      formData.hasInsurance === false &&
      (!formData.idFile || !formData.selfieFile)
    ) {
      setErrorMessage("Please upload your ID and a selfie with your ID.");
      return;
    }
    if (step === 3 && formData.hasInsurance === null) {
      setErrorMessage("Please select an insurance option.");
      return;
    }
    setStep((prev) => prev + 1);
    setErrorMessage("");
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
    setErrorMessage("");
  };

  const handleDateChange = (date, field) => {
    setFormData((prev) => ({ ...prev, [field]: date }));
  };

  const handleAddOnsChange = (addOn) => {
    setFormData((prev) => ({
      ...prev,
      addOns: prev.addOns.includes(addOn)
        ? prev.addOns.filter((item) => item !== addOn)
        : [...prev.addOns, addOn],
    }));
  };

  const handleFileUpload = (e, field) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, [field]: file }));
  };

  const handleFullCoverageToggle = () => {
    setFormData((prev) => ({
      ...prev,
      fullCoverage: !prev.fullCoverage,
    }));
  };

  const handleAgreement = () => {
    setStep((prev) => prev + 1);
  };

  const handleSendLink = () => {
    // This function simulates sending a link to the provided phone number.
    // In a real implementation, integrate with an SMS service here.
    if (phoneNumber) {
      alert(`A link to upload photos has been sent to ${phoneNumber}`);
    } else {
      alert("Please enter a valid phone number.");
    }
  };

  const totalRentalDays = Math.ceil(
    (formData.returnDate - formData.pickupDate) / (1000 * 60 * 60 * 24)
  );

  const addOnsPricing = {
    refuel: 60,
    boosterSeat: 5,
  };

  const addOnsTotal = formData.addOns.reduce((total, addOn) => {
    if (addOn === "boosterSeat") {
      return total + addOnsPricing[addOn] * totalRentalDays;
    }
    return total + (addOnsPricing[addOn] || 0);
  }, 0);

  const insuranceCost = formData.fullCoverage ? 25 * totalRentalDays : 0;

  const totalCost =
    (car?.price || 0) * totalRentalDays + addOnsTotal + insuranceCost;

  if (!car) {
    return <p>Loading car details...</p>;
  }

  return (
    <div
      style={{
        // Removed background image styling
        minHeight: "100vh",
        width: "100vw",
        color: "black"
      }}
    >
      <div style={{ maxWidth: "800px", margin: "auto", padding: "20px", color: "black" }}>
        <button
          onClick={() => window.history.back()}
          style={{
            background: "none",
            border: "none",
            fontSize: "1em",
            color: "black",
            cursor: "pointer",
            marginBottom: "10px",
          }}
        >
          ← Back
        </button>

        <h1 style={{ textAlign: "center", color: "black" }}>
          Rent {car.carName}
        </h1>

        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <img
            src={car.pictures?.[0] || "https://via.placeholder.com/300"}
            alt={car.carName}
            style={{ width: "300px", borderRadius: "5px", marginBottom: "10px" }}
          />
          <p style={{ fontWeight: "bold", color: "black" }}>
            ${car.price !== undefined ? car.price : "Price Not Available"}/day
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <progress value={step} max="6" style={{ width: "100%" }}></progress>
          <p style={{ textAlign: "center", marginTop: "5px", color: "black" }}>
            Step {step} of 6
          </p>
        </div>

        {step === 1 && (
          <div>
            <h3 style={{ textAlign: "center", color: "black" }}>
              Select Pickup and Return Dates
            </h3>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <div>
                <label style={{ color: "black" }}>Pickup{" "} </label>
                <DatePicker
                  selected={formData.pickupDate}
                  onChange={(date) => handleDateChange(date, "pickupDate")}
                  showTimeSelect
                  timeIntervals={60}
                  timeFormat="hh:mm aa"
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minTime={new Date(0, 0, 0, 7, 0)}
                  maxTime={new Date(0, 0, 0, 17, 0)}
                />
              </div>
              <span style={{ fontSize: "24px", color: "black" }}>→</span>
              <div>
                <label style={{ color: "black" }}>Return{" "} </label>
                <DatePicker
                  selected={formData.returnDate}
                  onChange={(date) => handleDateChange(date, "returnDate")}
                  showTimeSelect
                  timeIntervals={60}
                  timeFormat="hh:mm aa"
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minTime={new Date(0, 0, 0, 7, 0)}
                  maxTime={new Date(0, 0, 0, 17, 0)}
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 style={{ color: "black" }}>Add-Ons</h3>
            <p style={{ color: "black" }}>Select additional services:</p>
            <label style={{ color: "black" }}>
              <input
                type="checkbox"
                checked={formData.addOns.includes("refuel")}
                onChange={() => handleAddOnsChange("refuel")}
              />
              Refuel Pass ($60 one time)
            </label>
            <br />
            <label style={{ color: "black" }}>
              <input
                type="checkbox"
                checked={formData.addOns.includes("boosterSeat")}
                onChange={() => handleAddOnsChange("boosterSeat")}
              />
              Booster Seat ($5/day)
            </label>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3 style={{ color: "black" }}>Insurance and ID Verification</h3>
            <div style={{ marginBottom: "20px" }}>
              <button
                onClick={() =>
                  setFormData((prev) => ({ ...prev, hasInsurance: true }))
                }
                style={{
                  padding: "10px",
                  marginRight: "10px",
                  backgroundColor:
                    formData.hasInsurance === true ? "#0056b3" : "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                I have insurance
              </button>
              <button
                onClick={() =>
                  setFormData((prev) => ({ ...prev, hasInsurance: false }))
                }
                style={{
                  padding: "10px",
                  backgroundColor:
                    formData.hasInsurance === false ? "#0056b3" : "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                I don't have insurance
              </button>
            </div>

            {formData.hasInsurance === true && (
              <>
                <p style={{ color: "black" }}>Upload your insurance details:</p>
                <input
                  type="file"
                  accept="image/*"
                  capture
                  onChange={(e) => handleFileUpload(e, "insuranceFile")}
                />
              </>
            )}

            {formData.hasInsurance === false && (
              <p style={{ marginBottom: "10px", color: "black" }}>
                Full coverage is $25/day if you don't have insurance.
              </p>
            )}

            <div style={{ marginBottom: "20px" }}>
              <p style={{ marginBottom: "10px", fontSize: "1.2em", color: "black" }}>
                Upload your ID:
              </p>
              <img
                src={exampleImage}
                alt="Example ID Document"
                style={{
                  width: "200px",
                  height: "auto",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  marginTop: "10px",
                }}
              />
            </div>
            <p
              style={{
                fontSize: "0.9em",
                color: "gray",
                marginTop: "5px",
              }}
            >
              Example photo - please make sure your ID is on a flat surface and the picture is clear
            </p>
            <input
              type="file"
              accept="image/*"
              capture
              onChange={(e) => handleFileUpload(e, "idFile")}
            />
            <p style={{ fontSize: "1.2em", color: "black" }}>
              Upload a selfie with your ID:
            </p>
            <img
              src={exampleImage2}
              alt="Example selfie with ID"
              style={{
                width: "100px",
                height: "auto",
                marginTop: "10px",
                borderRadius: "5px",
              }}
            />
            <br />
            <p
              style={{
                fontSize: "0.9em",
                color: "gray",
                marginTop: "5px",
              }}
            >
              Example photo - please make sure both your ID and face are visible
            </p>
            <br />
            <input
              type="file"
              accept="image/*"
              capture
              onChange={(e) => handleFileUpload(e, "selfieFile")}
            />

            {/* New Section: Send Link to Phone */}
            <div style={{ marginTop: "20px" }}>
              <p style={{ color: "black" }}>
                Enter your phone number to receive an upload link:
              </p>
              <input
                type="tel"
                placeholder="Your phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                style={{ padding: "8px", width: "100%", marginBottom: "10px", color: "black" }}
              />
              <button
                onClick={handleSendLink}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Send Link
              </button>
            </div>

            {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}
          </div>
        )}

        {step === 4 && (
          <div>
            <h3 style={{ color: "black" }}>Terms of Service Agreement</h3>
            <p style={{ color: "black" }}>
              By continuing forward you agree to the{" "}
              <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>
              . By agreeing, you accept all of our terms and conditions.
            </p>
            <button
              onClick={handleAgreement}
              style={{
                padding: "10px",
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              I Agree
            </button>
          </div>
        )}

        {step === 5 && (
          <div>
            <h3 style={{ color: "black" }}>Review, Credit Card & Confirm</h3>
            <p style={{ color: "black" }}>Pickup Date: {formData.pickupDate.toDateString()}</p>
            <p style={{ color: "black" }}>Return Date: {formData.returnDate.toDateString()}</p>
            <p style={{ color: "black" }}>Total Cost: ${totalCost.toFixed(2)}</p>
            <p style={{ color: "black" }}>
              A $250 deductible will apply in case of any damage or incident.
            </p>
            <div>
              <h4 style={{ color: "black" }}>Enter Credit Card Information</h4>
              <input
                type="text"
                placeholder="Cardholder Name"
                value={formData.cardHolderName}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    cardHolderName: e.target.value,
                  }))
                }
                style={{ display: "block", marginBottom: "10px", width: "100%", padding: "8px", color: "black" }}
              />
              <input
                type="text"
                placeholder="Card Number"
                value={formData.cardNumber}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    cardNumber: e.target.value,
                  }))
                }
                style={{ display: "block", marginBottom: "10px", width: "100%", padding: "8px", color: "black" }}
              />
              <input
                type="text"
                placeholder="Expiry Date (MM/YY)"
                value={formData.cardExpiry}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    cardExpiry: e.target.value,
                  }))
                }
                style={{ display: "block", marginBottom: "10px", width: "100%", padding: "8px", color: "black" }}
              />
              <input
                type="text"
                placeholder="CVV"
                value={formData.cardCVV}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    cardCVV: e.target.value,
                  }))
                }
                style={{ display: "block", marginBottom: "10px", width: "100%", padding: "8px", color: "black" }}
              />
            </div>
            <button
              onClick={() => setStep(6)}
              style={{
                padding: "10px",
                backgroundColor: "#28a745",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                marginTop: "20px"
              }}
            >
              Pay Now
            </button>
          </div>
        )}

        {step === 6 && (
          <div>
            <h3 style={{ color: "black" }}>Confirmation</h3>
            <p style={{ color: "black" }}>
              Your rental has been confirmed! Thank you for your payment.
            </p>
          </div>
        )}

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          {step > 1 && step < 6 && (
            <button
              onClick={handleBack}
              style={{
                padding: "10px 20px",
                backgroundColor: "#ccc",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                marginRight: "10px",
              }}
            >
              Back
            </button>
          )}
          {step === 4 ? (
            <button
              onClick={handleAgreement}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              I Agree
            </button>
          ) : step < 5 && (
            <button
              onClick={handleNext}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RentalProcessPage;
